import { useMediaInactivityTracking } from 'hooks/useMediaInactivityTracking';
import useMixpanel from 'hooks/useMixpanel';
import { usePlaybookTheme } from 'playbook';
import React, { useEffect, useRef, useState } from 'react';
import { MPEventType } from 'types/mixpanel';

declare global {
  interface Window {
    _wq: unknown[];
  }
}

interface WistiaPlayerProps {
  autoFocus?: boolean;
  hashedId: string;
  videoId: string;
  videoFoam?: boolean;
  onPause?: (percentWatched: number) => void;
  onProgress?: (percentWatched: number) => void;
  mixPanelData?: Record<string, unknown>;
}

type TFunc = (arg1?: any, arg2?: any) => void;

type TDialog = {
  doClose: TFunc;
};

interface TVideo {
  remove: TFunc;
  duration: () => number;
  time: () => number;
  bind: (name: string, cb: TFunc) => void;
  controls: {
    settingsButton: {
      dialog: TDialog;
    };
    captionsButton: {
      dialog: TDialog;
    };
    smallPlayButton: {
      buttonElement: HTMLButtonElement;
    };
  };
}

const WistiaPlayer = ({
  autoFocus = true,
  hashedId,
  videoId,
  videoFoam = true,
  onPause = () => {},
  onProgress = () => {},
  mixPanelData = {},
}: WistiaPlayerProps) => {
  const track = useMixpanel();
  const theme = usePlaybookTheme();
  const [latestPlayerEvent, setLatestPlayerEvent] = useState('paused');
  const { updateMediaTimestamp, clearMediaTimestamp } =
    useMediaInactivityTracking();

  const hasBeenPausedRef = useRef(false);
  const trackVideoEvent = (
    eventName: string,
    eventType?: MPEventType,
  ): void => {
    track('User Action', {
      event_name: eventName,
      event_type: eventType || 'screen interaction',
      event_category: 'on demand',
      event_location: 'video_on_demand',
      content_id: videoId,
      ...mixPanelData,
    });
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing code prior to biome migration
  useEffect(() => {
    let handle: TVideo;
    let videoWatched = false;

    // Reset refs when video changes (hashedId is in dependency array)
    hasBeenPausedRef.current = false;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && handle) {
        handle.controls.settingsButton.dialog.doClose();
        handle.controls.captionsButton.dialog.doClose();
      }
    };

    window._wq = window._wq || [];
    window._wq.push({
      id: hashedId,
      options: {
        playerColor: theme.colors.aquamarine[300],
        resumable: false, // prevents wistia from caching the video progress if the user views it again later
      },
      onEmbedded: (video: TVideo) => {
        const videoLen = video.duration();
        handle = video;

        video.bind('enterfullscreen', () => {
          track('Screen View', {
            page_name: 'video_on_demand',
          });
        });

        video.bind('pause', () => {
          setLatestPlayerEvent('paused');
          // Clear timestamp immediately when paused so idle timer can trigger
          clearMediaTimestamp();
          const percWatched = video.time() / videoLen;
          if (percWatched <= 1) {
            onPause(percWatched);
          }
          hasBeenPausedRef.current = true;
          trackVideoEvent('video_pause');
        });
        video.bind('play', () => {
          setLatestPlayerEvent('playing');
          if (hasBeenPausedRef.current) {
            trackVideoEvent('video_resume');
          } else {
            trackVideoEvent('video_play');
          }
        });
        video.bind('end', () => {
          // Clear timestamp immediately when video ends so idle timer can trigger
          clearMediaTimestamp();
        });
        video.bind('seek', (seconds) => {
          setLatestPlayerEvent(`Seeked to ${seconds} seconds`);
        });
        video.bind('volumechange', (volume, isMuted) => {
          if (isMuted) {
            setLatestPlayerEvent('Volume muted');
          } else {
            setLatestPlayerEvent(
              `Volume set to ${(Number(volume) * 100).toFixed(0)}%`,
            );
          }
        });
        // secondchange only fires when video is playing - update timestamp to keep it fresh
        video.bind('secondchange', (seconds) => {
          updateMediaTimestamp();
          const percWatched = video.time() / videoLen;
          if (seconds % 10 === 0 && percWatched <= 1) {
            onProgress(percWatched);
          }
          if (percWatched >= 0.7 && !videoWatched) {
            videoWatched = true;
            trackVideoEvent('video_watched', 'active engagement');
          }
        });
        document.addEventListener('keydown', handleKeyDown);

        // Focus on the play button after the video is loaded
        if (autoFocus) {
          video.controls.smallPlayButton.buttonElement.focus();
        }
      },
    });

    return function onUnload() {
      if (handle) {
        handle.remove();
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hashedId, updateMediaTimestamp, clearMediaTimestamp]);
  return (
    <>
      <div
        className={`wistia_embed wistia_async_${hashedId} videoFoam=${videoFoam}`}
        style={{ height: '100%', width: '100%' }}
      />
      <div
        role="region"
        aria-live="polite"
        style={{ display: 'hidden' }}
        aria-label={latestPlayerEvent}
      />
    </>
  );
};

export default WistiaPlayer;
